import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { loadClarity, stopClarity } from "../utils/clarity";

const CookieBanner = () => {
  const { t, i18n } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Banner nur zeigen, wenn noch keine Wahl getroffen wurde
    const cookieConsent = localStorage.getItem("cookieConsent");
    setShowBanner(!cookieConsent);
  }, []);

  // "Cookie-Einstellungen" im Footer oeffnet das Banner erneut — auch dann,
  // wenn bereits eine Wahl gespeichert ist. Mehrfaches Ausloesen ist unkritisch.
  useEffect(() => {
    const reopen = () => setShowBanner(true);
    window.addEventListener("open-cookie-banner", reopen);
    return () => window.removeEventListener("open-cookie-banner", reopen);
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setShowBanner(false);
    loadClarity();
  };

  const handleDecline = () => {
    localStorage.setItem("cookieConsent", "declined");
    setShowBanner(false);
    stopClarity();
  };

  const handleLearnMore = () => {
    // Statische Datenschutzseite, Sprachweiche wie im Footer.
    window.location.href = i18n.language?.startsWith("de")
      ? "/privacy"
      : "/en/privacy";
  };

  const handleKeyDown = (event, action) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (action === "accept") {
        handleAccept();
      } else if (action === "decline") {
        handleDecline();
      } else if (action === "learnMore") {
        handleLearnMore();
      }
    }
  };

  // Function to reset cookie consent (for testing)
  const resetCookieConsent = () => {
    localStorage.removeItem("cookieConsent");
    setShowBanner(true);
  };

  // Add reset function to window for testing
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.resetCookieConsent = resetCookieConsent;
    }
  }, []);

  if (!showBanner) {
    return null;
  }

  return (
    <div
      className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:max-w-sm bg-[#0F1B2D]/95 backdrop-blur-md border border-[#C5A66A]/20 shadow-luxury rounded-xl z-50 p-4"
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-description"
      aria-label="Cookie consent banner"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div
            className="w-8 h-8 bg-gradient-to-br from-[#C5A66A] to-[#d4b886] rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            aria-hidden="true"
          >
            <svg
              className="w-4 h-4 text-[#0F1B2D]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3
              id="cookie-title"
              className="text-white font-semibold text-sm mb-1"
            >
              {t("cookieBanner.title")}
            </h3>
            <p
              id="cookie-description"
              className="text-gray-300 text-xs leading-relaxed"
            >
              {t("cookieBanner.description")}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <button
              onClick={handleAccept}
              onKeyDown={(e) => handleKeyDown(e, "accept")}
              className="flex-1 px-3 py-2 text-xs font-medium text-[#0F1B2D] bg-gradient-to-r from-[#C5A66A] to-[#d4b886] hover:from-[#d4b886] hover:to-[#C5A66A] rounded-lg transition-all duration-300 shadow-luxury hover:shadow-luxury-hover transform hover:scale-105"
              aria-label="Accept cookies and tracking"
            >
              {t("cookieBanner.accept")}
            </button>
            <button
              onClick={handleDecline}
              onKeyDown={(e) => handleKeyDown(e, "decline")}
              className="px-3 py-2 text-xs font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-[#C5A66A] rounded-lg transition-all duration-300"
              aria-label="Decline cookies and tracking"
            >
              {t("cookieBanner.decline")}
            </button>
          </div>
          <button
            onClick={handleLearnMore}
            onKeyDown={(e) => handleKeyDown(e, "learnMore")}
            className="text-xs text-[#C5A66A] hover:text-[#d4b886] underline transition-colors duration-300"
            aria-label="Learn more about privacy policy"
          >
            {t("cookieBanner.learnMore")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
